import React, { memo, useMemo } from 'react';

import { Checkbox as MaterialCheckbox, FormControl, FormControlLabel } from '@mui/material';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import FieldWrapper from '../FieldWrapper';
import HelperText from '../HelperText';

import { getStyles } from './CheckboxCDS.styles';

const CheckboxIcon = ({ size, fillColor }) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 18 18"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 4C1 2.34315 2.34315 1 4 1H14C15.6569 1 17 2.34315 17 4V14C17 15.6569 15.6569 17 14 17H4C2.34315 17 1 15.6569 1 14V4Z"
      fill={fillColor}
    />
    <path
      d="M1 4C1 2.34315 2.34315 1 4 1H14C15.6569 1 17 2.34315 17 4V14C17 15.6569 15.6569 17 14 17H4C2.34315 17 1 15.6569 1 14V4Z"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

CheckboxIcon.propTypes = {
  size: PropTypes.number.isRequired,
  fillColor: PropTypes.string,
};

const CheckedCheckbox = ({ size }) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 18 18"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.5 4C0.5 2.067 2.067 0.5 4 0.5H14C15.933 0.5 17.5 2.067 17.5 4V14C17.5 15.933 15.933 17.5 14 17.5H4C2.067 17.5 0.5 15.933 0.5 14V4Z"
      fill="currentColor"
    />
    <path
      d="M0.5 4C0.5 2.067 2.067 0.5 4 0.5H14C15.933 0.5 17.5 2.067 17.5 4V14C17.5 15.933 15.933 17.5 14 17.5H4C2.067 17.5 0.5 15.933 0.5 14V4Z"
      stroke="currentColor"
    />
    <path
      d="M4.5 9L7.5 12L13.5 6"
      stroke="#ffffff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

CheckedCheckbox.propTypes = {
  size: PropTypes.number.isRequired,
};

const slotProps = {
  typography: {
    className: 'checkbox-label',
  },
};

const Checkbox = ({
  value = false,
  label = '',
  fullWidth = false,
  helperText = '',
  error = false,
  noValidate = false,
  disabled = false,
  inputProps,
  containerClassName = '',
  labelPlacement = 'end',
  customStyles,
  columns = 1,
  width,
  dataTestId = '',
  iconProps,
  isFieldChanged = false,
  isHidden = false,
  ...props
}) => {
  const styles = useMemo(
    () => getStyles({ error, disabled, isFieldChanged, columns }),
    [error, disabled, isFieldChanged, columns],
  );

  const isChecked = (_.isBoolean(value) && value) || value === 'true';

  const extendedInputProps = useMemo(() => {
    if (inputProps && _.isObject(inputProps)) {
      return {
        ...inputProps,
        'data-test-checked': isChecked,
      };
    }

    return {
      'data-test-checked': isChecked,
    };
  }, [isChecked, inputProps]);

  const finalInputProps = useMemo(
    () => ({
      ...extendedInputProps,
      'data-novalidate': true,
    }),
    [extendedInputProps],
  );

  return (
    <FormControl
      className={clsx(containerClassName, 'justify-end')}
      columns={columns}
      component={FieldWrapper}
      customStyles={customStyles}
      error={error}
      fullWidth={fullWidth}
      isHidden={isHidden}
      width={width}
    >
      <FormControlLabel
        className="checkbox"
        control={
          <MaterialCheckbox
            disableFocusRipple
            disableRipple
            checked={isChecked}
            checkedIcon={<CheckedCheckbox className="checkbox-icon" size={18} {...iconProps} />}
            className="checkbox-control"
            data-testid={dataTestId}
            disabled={disabled}
            icon={<CheckboxIcon className="checkbox-icon" size={18} {...iconProps} />}
            inputProps={noValidate ? finalInputProps : extendedInputProps}
            sx={styles.checkbox}
            type="checkbox"
            {...props}
          />
        }
        disabled={disabled}
        label={label}
        labelPlacement={labelPlacement}
        slotProps={slotProps}
        sx={styles.controlLabel}
      />
      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </FormControl>
  );
};

Checkbox.propTypes = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isFieldChanged: PropTypes.bool,
  error: PropTypes.bool,
  noValidate: PropTypes.bool,
  fullWidth: PropTypes.bool,
  withStaticHelper: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  helperText: PropTypes.string,
  dataTestId: PropTypes.string,
  width: PropTypes.string,
  inputProps: PropTypes.object,
  labelPlacement: PropTypes.string,
  containerClassName: PropTypes.string,
  columns: PropTypes.number,
  customStyles: PropTypes.object,
  iconProps: PropTypes.object,
  isHidden: PropTypes.bool,
};

export default memo(Checkbox);
